import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  Table,
  TableBody,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableRow,
} from '@ui/v2/Table/Table'

import { PageQuery } from '@utils/ApiClient'
import { getCentralRowIdConditionally } from '@utils/getItemId'

import { TransactionDto } from '@model/TransactionDto'

import {
  TransactionHistoryEmptyState,
  TransactionHistoryRowCells,
} from '@shared/components/TransactionHistoryTable'
import styles from '@shared/components/TransactionHistoryTable/transactionHistoryTable.module.scss'

interface Props {
  data: TransactionDto[]
  pageQuery?: PageQuery
  activeFilters: number
  hasSearch?: boolean
  isLoading?: boolean
  setPageQuery?: (q: PageQuery) => void
  onRowClick: (tx: TransactionDto) => void
  selectedTransactionId?: string
  walletId?: string
  pageKey?: React.Key
}

export const TransactionHistoryTable: React.FC<Props> = (props) => {
  const {
    data,
    pageQuery,
    isLoading = false,
    activeFilters,
    hasSearch,
    selectedTransactionId,
    walletId,
    pageKey,
  } = props
  const { setPageQuery, onRowClick } = props
  const { t } = useTranslation()

  const setSort = (sort?: string | null, sortOrder?: 'ASC' | 'DESC') => {
    if (!setPageQuery) return
    setPageQuery({ ...(pageQuery || {}), sort: sort ?? undefined, sortOrder })
  }

  const hasActiveFilters = activeFilters > 0 || !!hasSearch

  const noResultsNode =
    data.length === 0 ? <TransactionHistoryEmptyState hasActiveFilters={hasActiveFilters} /> : null

  return (
    <Table isLoading={isLoading} noResults={noResultsNode} className={styles.table}>
      <TableHeader>
        <TableRow>
          <TableHeadSort
            id='TransactionType.name'
            align='left'
            width={190}
            className={styles.typeCell}
            sort={pageQuery?.sort}
            sortOrder={pageQuery?.sortOrder}
            setSort={setSort}
          >
            {t('Type')}
          </TableHeadSort>
          <TableHead align='right' width={144} className={styles.amountRow}>
            {t('Amount')}
          </TableHead>
          <TableHead width={32} className={styles.amountSpacerCell} />
          <TableHead align='left' width={180} className={styles.fromToCell}>
            {t('From')}
          </TableHead>
          <TableHead align='center' width={60} className={styles.arrowCell} />
          <TableHead align='left' width={180} className={styles.fromToCell}>
            {t('To')}
          </TableHead>
          <TableHeadSort
            id='TransactionState.name'
            align='left'
            width={154}
            className={styles.statusCell}
            sort={pageQuery?.sort}
            sortOrder={pageQuery?.sortOrder}
            setSort={setSort}
          >
            {t('Status')}
          </TableHeadSort>
          <TableHead align='left' className={styles.nameRow}>
            {t('Wallet.Reference number')}
          </TableHead>
          <TableHead width={40} />
        </TableRow>
      </TableHeader>

      {!!data.length && (
        <TableBody animateNewRows pageKey={pageKey}>
          {data.map((tx, index) => (
            <TableRow
              key={tx.id}
              {...getCentralRowIdConditionally(index, data.length, 'transaction-history')}
              selected={selectedTransactionId === tx.id}
              className={styles.clickableRow}
              onClick={() => onRowClick(tx)}
            >
              <TransactionHistoryRowCells tx={tx} context='client' walletId={walletId} />
            </TableRow>
          ))}
        </TableBody>
      )}
    </Table>
  )
}
